/**
 * The cover pool as the create modal uses it: read it, add to it, remove from it.
 *
 * ── The modal writes nothing about the PLAN, and that has not changed ────────────────────────
 * `useCreatePlanFlow`'s note still holds — no plan is created until the builder's first Zapisz. The
 * two writes here are about the trainer's own MEDIA LIBRARY, which exists independently of any
 * plan: a picture uploaded in this modal survives cancelling it, and shows up on the phone, because
 * `POST /plan-covers` adds to the trainer's pool rather than to a plan. That is the phone's
 * behaviour too, and it is why an abandoned modal is still free of consequence for the plan.
 *
 * ── A failed upload is SAID, unlike on the phone ─────────────────────────────────────────────
 * `CoverPickerSheet` swallows a failed upload to `null` so it can never block saving a plan. Here
 * there is nothing to block — the modal has no save — and a full-width form with room for a
 * sentence, so the refusal is drawn and announced instead of disappearing.
 */

import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  deletePlanCover,
  invalidatePlanCovers,
  planCoversQueryOptions,
  uploadPlanCover,
  type PlanCategoryKey,
} from '~/entities/plan-draft';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { prepareCover } from '~/shared/media/compressCover';
import { coverRefusalKey } from '~/shared/media/coverFile';

import {
  canAddCover,
  coverOptions,
  coverUploadErrorKey,
  ownCoverCount,
  type CoverOption,
} from '../model/coverPool';

export interface CoverPool {
  /** `pending` and `error` both draw the automatic option alone. */
  readonly status: 'pending' | 'error' | 'ready';
  readonly options: readonly CoverOption[];
  /** The trainer's own covers, and the ceiling the SERVER holds them against. */
  readonly ownCount: number;
  readonly ownLimit: number;
  /** Whether the add tile may act. At the ceiling it is drawn dimmed, never removed. */
  readonly canAdd: boolean;
  /** A write is in flight: the whole pool is inert until it lands. */
  readonly busy: boolean;
  /** The cover being removed right now, so its own tile can say so. */
  readonly removingId: number | null;
  /** Why the last write failed, already worded. `null` when nothing is wrong. */
  readonly error: string | null;
  /** The live region's text — the trainer's own acts, succeeding. */
  readonly announcement: string;
  /** Whether a cover is the trainer's own. `null` (automatic) never is. */
  readonly isOwn: (coverId: number | null) => boolean;
  readonly add: (file: File) => void;
  readonly remove: (coverId: number) => void;
}

export function useCoverPool(
  category: PlanCategoryKey,
  /** Select the picture that was just uploaded — the trainer chose it by choosing the file. */
  onUploaded: (coverId: number) => void,
  /** The cover the plan was carrying is gone; fall back to the automatic pick. */
  onDeleted: (coverId: number) => void,
): CoverPool {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { message, announce } = useAnnouncer();
  const covers = useQuery(planCoversQueryOptions(category));
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // Encoded BEFORE it is sent, exactly as the phone does it: longest edge 1600, JPEG at 0.8,
      // EXIF dropped with it. The backend reads JPEG and WebP only, so a PNG that went up raw came
      // back a 400 the trainer could do nothing about.
      const prepared = await prepareCover(file);
      if (!prepared.ok) throw new CoverRefused(t(coverRefusalKey(prepared.refusal)));
      const uploaded = await uploadPlanCover(prepared.file);
      await invalidatePlanCovers(queryClient);
      return uploaded;
    },
    onSuccess: (uploaded) => {
      setError(null);
      announce(t('web:planCreate.details.coverAdded'));
      if (typeof uploaded.id === 'number') onUploaded(uploaded.id);
    },
    onError: (failure: unknown) => {
      if (failure instanceof CoverRefused) {
        setError(failure.sentence);
        announce(failure.sentence);
        return;
      }
      // Branch on the CODE, never on the status: the media module answers 400 for three different
      // reasons and 429 for one, and only the coded ones can be worded usefully.
      const status = failure instanceof ApiError ? failure.status : 0;
      const code = failure instanceof ApiError ? failure.code : undefined;
      const sentence = t(coverUploadErrorKey(status, code));
      setError(sentence);
      announce(sentence);
    },
  });

  const remove = useMutation({
    mutationFn: async (coverId: number) => {
      await deletePlanCover(coverId);
      await invalidatePlanCovers(queryClient);
      return coverId;
    },
    onSuccess: (coverId) => {
      setError(null);
      announce(t('plans.builder.coverDeleteTitle'));
      onDeleted(coverId);
    },
    onError: () => {
      // The phone's own pair, and it says the useful half: the picture is still there.
      setError(t('plans.builder.coverDeleteFailedBody'));
      announce(t('plans.builder.coverDeleteFailedBody'));
    },
    onSettled: () => {
      setRemovingId(null);
    },
  });

  const own = ownCoverCount(covers.data);
  const busy = upload.isPending || remove.isPending;
  const options = coverOptions(covers.data);

  return {
    status: covers.isPending ? 'pending' : covers.isError ? 'error' : 'ready',
    options,
    ownCount: own.count,
    ownLimit: own.limit,
    canAdd: canAddCover(own.count, own.limit, busy),
    busy,
    removingId,
    error,
    announcement: message,
    isOwn: (coverId) =>
      coverId !== null && options.some((option) => option.coverId === coverId && option.isOwn),
    add: (file) => {
      setError(null);
      upload.mutate(file);
    },
    remove: (coverId) => {
      setRemovingId(coverId);
      remove.mutate(coverId);
    },
  };
}

/**
 * A picture refused before it was ever sent — the wrong kind of file, one the browser cannot draw,
 * or one still over 10 MiB after re-encoding.
 *
 * It travels as a throw so the local refusal and the server's share one failure path; the sentence
 * is already resolved, because `mutationFn` is the only place that knows which refusal it was.
 */
class CoverRefused extends Error {
  readonly sentence: string;

  constructor(sentence: string) {
    super(sentence);
    this.name = 'CoverRefused';
    this.sentence = sentence;
  }
}
